import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DarkModeContext } from "../../context/DarkModeContext";
import { getToken } from "../../utils/auth";
import { defaultFilters, fetchDashboard } from "./dashboardApi";
import { Icono } from "./iconos";
import {
    ZMLayout,
    ZMCard,
    ZMMetricCard,
    ZMTable,
    ZMBadge,
    ZMLoadingState,
    ZMErrorState,
    ZMEmptyState,
} from "./components/ZMUI";

const fmt = (n, dec = 0) =>
    n == null ? "0" : Number(n).toLocaleString("es-AR", { minimumFractionDigits: dec, maximumFractionDigits: dec });
const fmtPct = (n) => `${fmt(n || 0, 2)}%`;

export default function ZMClosers() {
    const { darkMode } = useContext(DarkModeContext);
    const navigate = useNavigate();
    const [cargando, setCargando] = useState(true);
    const [error, setError] = useState("");
    const [datos, setDatos] = useState({
        kpis: {},
        closersPerformance: [],
        qualityChecklist: [],
        opportunities: [],
        completionRate: 0,
    });

    const cargar = async () => {
        const token = getToken();
        if (!token) {
            navigate("/");
            return;
        }

        setCargando(true);
        setError("");
        try {
            const payload = await fetchDashboard("closers", token, defaultFilters());
            setDatos(payload || {});
        } catch (e) {
            if (e?.code === 401 || e?.code === 403 || e?.message === "unauthorized") {
                navigate("/");
                return;
            }
            setError("No se pudo cargar el dashboard de closers.");
        } finally {
            setCargando(false);
        }
    };

    useEffect(() => {
        cargar();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigate]);

    if (cargando) return <ZMLoadingState label="Cargando closers..." fullScreen />;
    if (error) return <ZMErrorState message={error} onRetry={cargar} />;

    const kpis = datos.kpis || {};
    const closers = datos.closersPerformance || datos.closers || [];
    const checklist = datos.qualityChecklist || [];
    const opportunities = datos.opportunities || [];

    return (
        <ZMLayout
            title="Closers y oportunidades de mejora"
            subtitle="Seguimiento del equipo comercial, SLA y calidad de la data."
            right="Actualizado"
            className={darkMode ? "container-dark" : ""}
        >
            <div className="zm-grid-metrics">
                <ZMMetricCard icon={<Icono.Users />} label="Closers activos" value={fmt(kpis.closersActivos, 0)} />
                <ZMMetricCard icon={<Icono.Clock />} label="Tiempo medio" value={kpis.tiempoMedioRespuesta ? `${fmt(kpis.tiempoMedioRespuesta, 1)} min` : "sin datos"} />
                <ZMMetricCard icon={<Icono.Phone />} label="Contactabilidad" value={fmtPct(kpis.contactabilidad)} />
                <ZMMetricCard icon={<Icono.Target />} label="Cierre a demo" value={fmtPct(kpis.cierreDemo)} />
                <ZMMetricCard icon={<Icono.CheckCircle />} label="Cierre final" value={fmtPct(kpis.cierreFinal)} />
                <ZMMetricCard icon={<Icono.BarChart />} label="Completitud CRM" value={fmtPct(datos.completionRate)} />
            </div>

            <div className="row g-3 mb-3">
                <div className="col-12 col-xl-8">
                    <ZMCard title="Rendimiento por closer">
                        <ZMTable
                            emptyText="Sin closers para mostrar."
                            columns={[
                                { key: "closer", header: "Closer" },
                                { key: "leadsAsignados", header: "Leads", cellClassName: "text-end", render: (r) => fmt(r.leadsAsignados, 0) },
                                { key: "contactados", header: "Contactados", cellClassName: "text-end", render: (r) => fmt(r.contactados, 0) },
                                { key: "calificados", header: "Calificados", cellClassName: "text-end", render: (r) => fmt(r.calificados, 0) },
                                { key: "demos", header: "Demos", cellClassName: "text-end", render: (r) => fmt(r.demos, 0) },
                                { key: "cerrados", header: "Cerrados", cellClassName: "text-end", render: (r) => fmt(r.cerrados, 0) },
                                { key: "tasaContacto", header: "Tasa contacto", cellClassName: "text-end", render: (r) => fmtPct(r.tasaContacto) },
                                { key: "tasaCierre", header: "Tasa cierre", cellClassName: "text-end", render: (r) => fmtPct(r.tasaCierre) },
                                { key: "tiempoMedioRespuestaMinutos", header: "Tiempo respuesta", cellClassName: "text-end", render: (r) => r.tiempoMedioRespuestaMinutos ? `${fmt(r.tiempoMedioRespuestaMinutos, 1)} min` : "sin datos" },
                            ]}
                            rows={closers}
                        />
                    </ZMCard>
                </div>
                <div className="col-12 col-xl-4">
                    <ZMCard title="Oportunidades de mejora">
                        {opportunities.length === 0 ? (
                            <ZMEmptyState title="Sin oportunidades abiertas" hint="No se detectaron desajustes de seguimiento o calidad en este momento." />
                        ) : (
                            <div className="zm-alert-stack">
                                {opportunities.map((item, index) => (
                                    <div key={`${item.titulo}-${index}`} className="zm-alert-item">
                                        <div className="d-flex justify-content-between gap-2">
                                            <div>
                                                <strong>{item.titulo}</strong>
                                                <p>{item.descripcion}</p>
                                            </div>
                                            <ZMBadge status={item.prioridad === "alta" ? "rechazado" : item.prioridad === "media" ? "contactado" : "cerrado"} label={item.prioridad} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </ZMCard>
                </div>
            </div>

            <div className="row g-3">
                <div className="col-12">
                    <ZMCard title="Checklist de calidad de datos" subtitle="Campos clave para que el CRM sea analizable y accionable">
                        {checklist.length === 0 ? (
                            <ZMEmptyState title="Sin checklist disponible" hint="No hay leads en el rango actual para medir completitud." />
                        ) : (
                            <div className="zm-quality-grid">
                                {checklist.map((item) => {
                                    const completo = Number(item.porcentaje || 0) >= 80;
                                    return (
                                        <div key={item.clave} className="zm-quality-item">
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <strong>{item.label}</strong>
                                                <ZMBadge status={completo ? "cerrado" : "contactado"} label={completo ? "Completo" : "Incompleto"} />
                                            </div>
                                            <div className="zm-progress-line">
                                                <div style={{ width: `${Math.max(6, Number(item.porcentaje || 0))}%` }} />
                                            </div>
                                            <small>{fmtPct(item.porcentaje)}</small>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </ZMCard>
                </div>
            </div>
        </ZMLayout>
    );
}
